import type { AppearanceRow } from '@setplex/tria-api'
import { match, nil, onTerms, payload, variant, type VariantOf } from 'variant'
import type { RowsPage, TGetRowsFx } from './index.h'

export { isType } from 'variant'

// RowCacheByModule = Record<Identifier, Map<Id, Row>> // Id - row id
export type RowCacheByModule = Record<string, RowStructure | null>

export type RowStructure = Map<string, Row>

export type Row = {
  desc: AppearanceRow
  data: RowData
  hasMore?: boolean
}

export type ViewRow = {
  id: string
  content: unknown[]
  isMoreThanOnePage: boolean
  appearance: AppearanceRow
  type: Row['data']['type']
}

export type RowData = VariantOf<typeof RowData>
export const RowData = variant(
  onTerms(({ T }) => ({
    Empty: nil,
    Loading: nil,
    Failed: payload<unknown>(),
    Dirty: payload(T),
    Updating: payload(T),
    Awaiting: payload(T),
    Ready: payload(T),
  }))
)

export const { Empty, Loading, Failed, Dirty, Updating, Awaiting, Ready } =
  RowData

//
// row transition helpers
//

/**
 * Change row to dirty state
 */
export function toDirty(row: Row): Row {
  const { refreshable } = row.desc
  return {
    ...row,
    data: match(row.data, {
      Empty: (data) => data, // leave as is
      Loading: (data) => (refreshable ? Empty() : data), // if refreshable, drop loading state and reset
      Failed: () => Empty(), // reset failed state -> try to reload
      Dirty: (data) => data, // leave as is
      Updating: (data) => (refreshable ? Empty() : data), // if refreshable, drop updating state and reset
      Awaiting: (data) => (refreshable ? Dirty(data.payload) : data), // if refreshable, set dirty
      Ready: (data) => (refreshable ? Dirty(data.payload) : data), // if refreshable, set dirty
    }),
  }
}

/**
 * Change row to loading state
 */
export function toLoading(row: Row): Row {
  return {
    ...row,
    data: match(row.data, {
      Empty: () => Loading(),
      Loading: (data) => data, // leave as is
      Failed: () => Loading(),
      Dirty: (data) => Updating(data.payload),
      Updating: (data) => data, // leave as is
      Awaiting: (data) => Updating(data.payload), // do not erase!
      Ready: (data) => Updating(data.payload), // do not erase!
    }),
  }
}

export const filterRowsVisibility = (
  rows: RowsPage,
  source: TGetRowsFx['source']
) => {
  const { modules, features, options, isGuest } = source

  // filter rows (default and custom both)
  return rows.content.filter((row) => {
    // filter rows, disabled by flags
    // biome-ignore format: better readable this way
    if (
        (row.contentType === 'TV_CHANNEL' && !modules.tvEnabled) ||
        (row.contentType === 'TV_BUNDLE' && !modules.tvEnabled) ||
        (row.contentType === 'TV_SHOW' && !modules.tvShowEnabled) ||
        (row.contentType === 'TV_SHOW_BUNDLE' && !modules.tvShowEnabled) ||
        (row.contentType === 'VOD' && !modules.vodEnabled) ||
        (row.contentType === 'VOD_BUNDLE' && !modules.vodEnabled) ||
        (row.contentType === 'LIVE_EVENT' && !modules.liveEventEnabled) ||
        (row.contentType === 'PARTNER_PRODUCT' && !features.partnersProgramEnabled) ||
        (row.localizedTitleKey === 'movies_featured' && !options.featuredCarouselsEnabled) ||
        (row.localizedTitleKey === 'series_featured' && !options.featuredCarouselsEnabled) ||
        (row.localizedTitleKey === 'channel_featured' && !options.featuredCarouselsEnabled)
      ) return false

    // filter rows, unavailable for guests
    // biome-ignore format: better readable this way
    if (isGuest && (
        row.localizedTitleKey === 'continue_watching' ||
        row.localizedTitleKey === 'live_channels_recently_watched' ||
        row.localizedTitleKey === 'channel_favorites' ||
        row.localizedTitleKey === 'movies_favorite' ||
        row.localizedTitleKey === 'series_favorite'
      )) return false

    return true
  })
}
